import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Empty,
  Popconfirm,
  Select,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import {
  fetchAssessmentDebug,
  fetchAssessmentValidation,
  recomputeAssessmentOptimal,
  selectors,
  AssessmentDebugOptimalLine,
  AssessmentDebugOptimalBudget,
  AssessmentDebugOptimalDetailLine,
  AssessmentDebugPeriod,
  AssessmentDebugPosition,
  AssessmentDebugSummary,
  AssessmentDebugNews,
  AssessmentDebugConviction,
  AssessmentDebugVerdict,
  AssessmentDebugMetric,
  AssessmentValidationInstrument,
} from 'state';
import { formatters } from 'services';
import { ConvictionCoverageNote, ParticipationNote } from 'components';
import type { TableColumnsType as ColumnsType } from 'antd';

// Admin-only QA/inspection surface: raw resolver state, the per-period debug
// ledger and the scenario-level key validation. Dense tables, no charts. Data
// contract: pm.api GET /assessment_sessions/:session_id/debug/?player= and
// GET /assessment_sessions/validation/?scenario=.

const num1 = (value: unknown) =>
  typeof value === 'number' ? formatters.commasExact1Digit(value) : '-';
const num2 = (value: unknown) =>
  typeof value === 'number' ? formatters.commasExact2Digits(value) : '-';
const signed2 = (value: number) =>
  (value > 0 ? '+' : '') + formatters.commasExact2Digits(value);

const pct1 = (value: unknown) =>
  typeof value === 'number' ? formatters.percent1Digit(value) : '-';

/** Verdict wording -> colour. Only the two "the model wanted more" cases warn. */
const whyColor = (why: string) => {
  if (why.startsWith('cap-saturated')) return 'orange';
  if (why.startsWith('scaled down')) return 'gold';
  if (why.startsWith('no signal')) return 'default';
  if (why.startsWith('premium-capped')) return 'orange';
  return 'green';
};

/** Used/limit with a share bar — the budget view of one portfolio check. */
const BudgetBar: React.FC<{ budget: AssessmentDebugOptimalBudget }> = ({
  budget,
}) => {
  const share = typeof budget.pct === 'number' ? budget.pct : 0;
  const over = share > 1;
  return (
    <div className="assessment-debug-budget">
      <div className="assessment-debug-budget-head">
        <Typography.Text>{budget.label}</Typography.Text>
        <Typography.Text type={over ? 'danger' : 'secondary'}>
          {`${num1(budget.used)} / ${num1(budget.limit)} (${pct1(budget.pct)})`}
        </Typography.Text>
      </div>
      <div className="assessment-debug-budget-track">
        <div
          className={
            over
              ? 'assessment-debug-budget-fill is-over'
              : 'assessment-debug-budget-fill'
          }
          style={{ width: `${Math.min(Math.abs(share), 1) * 100}%` }}
        />
      </div>
      <div className="assessment-debug-budget-foot">
        <Tag color={budget.source === 'session' ? 'blue' : 'default'}>
          {budget.source === 'session'
            ? 'limit from this session'
            : 'limit is a model value'}
        </Tag>
        {budget.equation ? (
          <Typography.Text type="secondary" className="assessment-debug-equation">
            <b>How it is measured:</b> {budget.equation}
          </Typography.Text>
        ) : null}
      </div>
    </div>
  );
};

/** Consistent accordion header: a short name plus one line saying what the
 *  section answers. Long sentences as labels made the panel unscannable. */
const sectionLabel = (title: string, hint: string, extra?: React.ReactNode) => (
  <div className="assessment-debug-section-label">
    <div>
      <Typography.Text strong>{title}</Typography.Text>
      <Typography.Text type="secondary"> — {hint}</Typography.Text>
    </div>
    {extra ? <div className="assessment-debug-section-extra">{extra}</div> : null}
  </div>
);

/** One number in the orientation strip. */
const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  /** A node under the hint — used for the participation flag on Composite. */
  extra?: React.ReactNode;
}> = ({ label, value, hint, extra }) => (
  <div className="assessment-debug-stat">
    <div className="assessment-report-card-label">{label}</div>
    <div className="assessment-report-card-value">{value}</div>
    {hint ? (
      <Typography.Text type="secondary" style={{ fontSize: 11 }}>
        {hint}
      </Typography.Text>
    ) : null}
    {extra ? <div className="assessment-debug-stat-extra">{extra}</div> : null}
  </div>
);

const statusColor = (status?: string) => {
  if (status === 'done') return 'green';
  if (status === 'failed') return 'red';
  if (status === 'running') return 'blue';
  return 'default';
};

// "US2Y -120.0 $m" / "Call 0.5 $k/bp (prem 0.012)"
const positionLine = (position: AssessmentDebugPosition) => {
  const base = `${position.code} ${formatters.commasExact1Digit(
    position.position,
  )} ${position.unit}`;
  return position.premium_m !== null && position.premium_m !== undefined
    ? `${base} (prem ${formatters.commasExact3Digits(position.premium_m)})`
    : base;
};

/** Compact multi-line cell used by the ledger's positions/PCS/optimal cells. */
const Lines: React.FC<{ lines: string[] }> = ({ lines }) =>
  lines.length ? (
    <div className="assessment-debug-lines">
      {lines.map((line, index) => (
        <div key={`${line}-${index}`}>{line}</div>
      ))}
    </div>
  ) : (
    <>-</>
  );

// Summary aggregates in display order with their soft-limit references
// (pm.api engine/reference_model.py RiskLimits). Label-only: the limit is a
// reference in the header, exceeding just switches to the warning text class.
// weighted_excess is a score, not a limited exposure - no reference.
// Summary column -> the framework check that limits it. The LIMIT VALUE is
// never hardcoded here: it comes from risk_framework.portfolio_rows, which is
// derived from the session (see the "Risk framework" menu). Hardcoding it is
// exactly the bug this replaced — the panel showed the reference 500 while
// scoring used the session's 2000.
const AGGREGATES: {
  key: keyof AssessmentDebugSummary;
  label: string;
  limitKey?: string;
}[] = [
  { key: 'gross_cash', label: 'Total gross cash', limitKey: 'total_gross_cash_m' },
  { key: 'bond_gross', label: 'Bond book', limitKey: 'bond_gross_m' },
  { key: 'net_dv01', label: 'Net DV01', limitKey: 'net_dv01_k_per_bp' },
  { key: 'gross_dv01', label: 'Gross DV01', limitKey: 'gross_dv01_k_per_bp' },
  { key: 'equity_gross', label: 'Equity gross', limitKey: 'equity_gross_delta_m' },
  { key: 'equity_net', label: 'Equity net', limitKey: 'equity_net_delta_m' },
  { key: 'fx_gross', label: 'FX book', limitKey: 'fx_gross_m' },
  { key: 'common_usd', label: 'Same-direction USD', limitKey: 'same_direction_usd_m' },
  { key: 'oil_gross', label: 'Commodities', limitKey: 'oil_gross_m' },
  { key: 'option_premium', label: 'Option premium', limitKey: 'option_premium_m' },
  { key: 'stress_loss', label: 'Stress loss', limitKey: 'portfolio_stress_loss_m' },
  { key: 'short_book', label: 'Short pool', limitKey: 'short_book_m' },
  { key: 'weighted_excess', label: 'Weighted excess' },
];

const WEIGHTED_EXCESS_TIP =
  'How far outside the limits the book sat, all breaches added up. Each ' +
  'breach counts as the fraction it went over by (a book 10% over one limit ' +
  'adds 0.10), and the more serious checks count double. 0 means the book ' +
  'stayed inside every single limit — which is what you want to see.';

// --- Pillar 1 evidence ------------------------------------------------------
// |ICS| >= 0.25 is the benchmark's relevance bar (engine
// score_mapping_for_period). A pair is a HIT when the candidate linked a
// headline to a relevant instrument, FALSE when they linked an irrelevant one,
// MISSED when they never made a relevant link.
const RELEVANT_ICS = 0.25;

const icsLine = (news: AssessmentDebugNews) =>
  Object.entries(news.ics)
    .filter(([, value]) => typeof value === 'number' && Math.abs(value) >= 0.01)
    .sort((a, b) => Math.abs(b[1] as number) - Math.abs(a[1] as number))
    .map(
      ([code, value]) =>
        `${code} ${signed2(value as number)}${
          Math.abs(value as number) >= RELEVANT_ICS ? '*' : ''
        }`,
    );

// Only the instruments the news actually moves (|ICS| >= 0.25). The full
// picture — including the true negatives — lives in the expanded row, so the
// table itself stays one line per headline instead of ten.
const materialLine = (news: AssessmentDebugNews) =>
  Object.entries(news.ics)
    .filter(([, v]) => typeof v === 'number' && Math.abs(v as number) >= RELEVANT_ICS)
    .sort((a, b) => Math.abs(b[1] as number) - Math.abs(a[1] as number))
    .map(([code, v]) => `${code} ${signed2(v as number)}`);

const countTag = (n: number, color: string, label: string) =>
  n > 0 ? (
    <Tag color={color} key={label}>
      {`${n} ${label}`}
    </Tag>
  ) : null;

const newsColumns: ColumnsType<AssessmentDebugNews> = [
  {
    title: 'Headline',
    key: 'headline',
    width: '46%',
    render: (_, row) => (
      <div>
        <div>{row.headline || row.news_code}</div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {row.theme ? `${row.theme} · ` : ''}
          {row.news_code}
        </Typography.Text>
      </div>
    ),
  },
  {
    title: (
      <Tooltip
        title={
          'ICS = Instrument Conviction Score: how strongly THIS one headline ' +
          'moves each instrument, from -1 to +1. Only instruments at 0.25 or ' +
          'above (either direction) count as genuinely moved — those are the ' +
          'ones a trainee was expected to link the headline to.'
        }
      >
        <span>Moves these (ICS)</span>
      </Tooltip>
    ),
    key: 'material',
    width: '28%',
    render: (_, row) => {
      const lines = materialLine(row);
      return lines.length ? (
        <span>{lines.join('  ·  ')}</span>
      ) : (
        <Typography.Text type="secondary">nothing material</Typography.Text>
      );
    },
  },
  {
    title: 'Outcome',
    key: 'outcome',
    width: '26%',
    render: (_, row) => (
      <Tooltip title="Expand the row for every instrument and why">
        <span>
          {[
            countTag(row.hits.length, 'green', 'hit'),
            countTag(row.false.length, 'orange', 'false'),
            countTag(row.missed.length, 'red', 'missed'),
          ].filter(Boolean)}
          {!row.hits.length && !row.false.length && !row.missed.length && (
            <Typography.Text type="secondary">nothing to score</Typography.Text>
          )}
        </span>
      </Tooltip>
    ),
  },
];

const convictionColumns: ColumnsType<AssessmentDebugConviction> = [
  {
    title: 'Instrument',
    key: 'name',
    render: (_, row) => (
      <Tooltip title={row.code}>
        <span>{row.name || row.code}</span>
      </Tooltip>
    ),
  },
  {
    title: 'What happened',
    key: 'explain',
    render: (_, row) => (
      <Typography.Text type="secondary">{row.explain}</Typography.Text>
    ),
  },
  {
    title: (
      <Tooltip
        title={
          'Where the trainee set their own conviction dial for this ' +
          'instrument, on the same -5 to +5 scale as the benchmark. "not ' +
          'set" means they left it alone, which scores as no view.'
        }
      >
        <span>Slider</span>
      </Tooltip>
    ),
    key: 'slider',
    render: (_, row) =>
      row.slider === null || row.slider === undefined ? (
        <Typography.Text type="secondary">not set</Typography.Text>
      ) : (
        signed2(row.slider)
      ),
  },
  {
    title: (
      <Tooltip
        title={
          'What the model concluded from the same news, -5 to +5. The gap ' +
          'between this and the slider is what Pillar 1 scores.'
        }
      >
        <span>Benchmark PCS</span>
      </Tooltip>
    ),
    key: 'pcs',
    render: (_, row) => (typeof row.pcs === 'number' ? signed2(row.pcs) : '-'),
  },
  {
    title: (
      <Tooltip
        title={
          'How much the underlying news pointed the same way, 0 to 1. Near 1 ' +
          'the story was one-sided; near 0 the headlines contradicted each ' +
          'other. Low agreement shrinks the position the model asks for, and ' +
          'softens how hard a trainee is judged for disagreeing.'
        }
      >
        <span>Agreement</span>
      </Tooltip>
    ),
    key: 'agreement',
    render: (_, row) => num2(row.agreement),
  },
  {
    title: (
      <Tooltip
        title={
          'Simply: were they on the right side? Aligned means the trainee and ' +
          'the model wanted the same direction, whatever the size.'
        }
      >
        <span>Direction</span>
      </Tooltip>
    ),
    key: 'direction_ok',
    render: (_, row) =>
      row.direction_ok === null || row.direction_ok === undefined ? (
        '-'
      ) : row.direction_ok ? (
        <Tag color="green">aligned</Tag>
      ) : (
        <Tag color="red">opposed</Tag>
      ),
  },
  { title: 'Gap', key: 'gap', render: (_, row) => num2(row.gap) },
];

const VERDICT_COLOR: { [k: string]: string } = {
  'true positive': 'green',
  'false positive': 'orange',
  'false negative': 'red',
  'true negative': 'default',
};

// Every instrument's outcome for one headline, in words — the four quadrants,
// with PM's own asset names so a reviewer never has to decode engine codes.
const verdictColumns: ColumnsType<AssessmentDebugVerdict> = [
  {
    title: 'Instrument',
    key: 'name',
    render: (_, row) => (
      <Tooltip title={row.code}>
        <span>{row.name}</span>
      </Tooltip>
    ),
  },
  {
    title: 'ICS',
    key: 'ics',
    render: (_, row) => (typeof row.ics === 'number' ? signed2(row.ics) : '-'),
  },
  {
    title: 'Moves it?',
    key: 'material',
    render: (_, row) =>
      row.material ? <Tag color="blue">material</Tag> : <Tag>immaterial</Tag>,
  },
  {
    title: 'Candidate',
    key: 'linked',
    render: (_, row) => (row.linked ? 'linked' : 'left alone'),
  },
  {
    title: 'Verdict',
    key: 'verdict',
    render: (_, row) => (
      <Tooltip title={row.explain}>
        <Tag color={VERDICT_COLOR[row.verdict]}>{row.verdict}</Tag>
      </Tooltip>
    ),
  },
];

// One pillar's components for one period: what it measures (hover), its
// weight, the value, and the points it contributed to the 0-100 score.
const metricColumns: ColumnsType<AssessmentDebugMetric> = [
  {
    title: 'Metric',
    key: 'label',
    width: '26%',
    render: (_, row) => (
      <Tooltip title={row.tooltip || row.meaning}>
        <span style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>
          {row.label}
        </span>
      </Tooltip>
    ),
  },
  {
    title: 'What it measures',
    key: 'meaning',
    width: '44%',
    render: (_, row) => (
      <Typography.Text type="secondary">{row.meaning}</Typography.Text>
    ),
  },
  {
    title: 'Weight',
    key: 'weight',
    width: '10%',
    render: (_, row) =>
      row.weight === null ? (
        <Typography.Text type="secondary">diagnostic</Typography.Text>
      ) : (
        formatters.percent2Digit(row.weight)
      ),
  },
  { title: 'Value', key: 'value', width: '10%', render: (_, row) => num2(row.value) },
  {
    title: 'Points added',
    key: 'contribution',
    width: '10%',
    render: (_, row) =>
      row.contribution === null ? '-' : num2(row.contribution),
  },
];

/** "Shape of the book" ratios rendered as a labelled table — these used to
 *  exist only inside a raw aggregates dump. */
const shapeColumns: ColumnsType<{
  key: string;
  label: string;
  value: number | null;
  meaning: string;
}> = [
  { title: 'Measure', dataIndex: 'label', key: 'label', width: 220 },
  {
    title: 'Value',
    key: 'value',
    width: 110,
    align: 'right',
    render: (_, row) =>
      typeof row.value === 'number' ? (
        <Typography.Text strong>{num2(row.value)}</Typography.Text>
      ) : (
        '-'
      ),
  },
  {
    title: 'How it is calculated',
    dataIndex: 'equation',
    key: 'equation',
    width: 260,
    render: (equation: string | null) =>
      equation ? (
        <Typography.Text code style={{ fontSize: 12 }}>
          {equation}
        </Typography.Text>
      ) : (
        '-'
      ),
  },
  { title: 'What it tells you', dataIndex: 'meaning', key: 'meaning' },
];

/** One optimal-book line, end to end: signal -> wanted size -> final size ->
 *  risk taken. Tooltips come from the backend glossary (single source). */
const optimalDetailColumns = (
  tips: { [key: string]: string },
  names: { [code: string]: string },
): ColumnsType<AssessmentDebugOptimalDetailLine> => [
  {
    title: 'Instrument',
    dataIndex: 'code',
    key: 'code',
    width: 150,
    render: (code: string) => (
      <Tooltip title={names[code] || code}>
        <span>{names[code] || code}</span>
      </Tooltip>
    ),
  },
  {
    title: <Tooltip title="The benchmark's conviction: how strongly the news says to be positioned, from -5 to +5.">
      <span>PCS</span>
    </Tooltip>,
    dataIndex: 'pcs',
    key: 'pcs',
    width: 80,
    align: 'right',
    render: (value: number | null) =>
      typeof value === 'number' ? signed2(value) : '-',
  },
  {
    title: <Tooltip title="How much the analyst views agreed. Low agreement shrinks the position.">
      <span>Agreement</span>
    </Tooltip>,
    dataIndex: 'agreement',
    key: 'agreement',
    width: 100,
    align: 'right',
    render: num2,
  },
  {
    title: <Tooltip title={tips.band_util}><span>Band</span></Tooltip>,
    dataIndex: 'band_util',
    key: 'band_util',
    width: 90,
    align: 'right',
    render: pct1,
  },
  {
    title: <Tooltip title={tips.conviction_util}><span>Wanted</span></Tooltip>,
    dataIndex: 'conviction_util',
    key: 'conviction_util',
    width: 90,
    align: 'right',
    render: pct1,
  },
  {
    title: <Tooltip title={tips.final_util}><span>Final (of own cap)</span></Tooltip>,
    dataIndex: 'final_util',
    key: 'final_util',
    width: 130,
    align: 'right',
    render: pct1,
  },
  {
    title: <Tooltip title="The instrument's own limit for THIS session: class limit % ÷ margin %.">
      <span>Cap ($m)</span>
    </Tooltip>,
    dataIndex: 'cap_m',
    key: 'cap_m',
    width: 110,
    align: 'right',
    render: num1,
  },
  {
    title: 'Target',
    key: 'target',
    width: 150,
    align: 'right',
    render: (_: unknown, row: AssessmentDebugOptimalDetailLine) =>
      typeof row.target === 'number'
        ? `${signed2(row.target)}${row.unit ? ` ${row.unit}` : ''}`
        : '-',
  },
  {
    title: (
      <Tooltip
        title={
          'What this line alone would lose in one standard bad day. For a bond: ' +
          'position x its DV01 per $1m x its shock in basis points / 1000. For ' +
          'anything else: position x its percentage shock. A bought option ' +
          'loses at most its premium. Direction is ignored — a short loses when ' +
          'the shock goes the other way. Expand the row for this line\'s own sum.'
        }
      >
        <span>Stress ($m)</span>
      </Tooltip>
    ),
    key: 'stress_m',
    width: 150,
    align: 'right',
    render: (_: unknown, row: AssessmentDebugOptimalDetailLine) => (
      <div>
        <div>{num1(row.stress_m)}</div>
        {typeof row.stress_shock === 'number' && row.stress_shock !== 0 ? (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {row.shock_unit === 'bp'
              ? `shock ${row.stress_shock.toFixed(0)}bp`
              : `shock ${(row.stress_shock * 100).toFixed(0)}%`}
          </Typography.Text>
        ) : null}
      </div>
    ),
  },
  {
    title: <Tooltip title={tips.stress_share}><span>% of budget</span></Tooltip>,
    dataIndex: 'stress_share',
    key: 'stress_share',
    width: 110,
    align: 'right',
    render: pct1,
  },
  {
    title: <Tooltip title={tips.optimal_why}><span>What set the size</span></Tooltip>,
    dataIndex: 'why',
    key: 'why',
    width: 220,
    render: (why: string) => <Tag color={whyColor(why)}>{why}</Tag>,
  },
];

/** "What the trainer set" — value + why it matters, in words. */
const settingColumns: ColumnsType<{
  label: string;
  value: number | boolean | null;
  unit: string;
  note: string;
}> = [
  { title: 'Setting', dataIndex: 'label', key: 'label', width: 200 },
  {
    title: 'Value',
    key: 'value',
    width: 130,
    align: 'right',
    render: (_, row) => {
      if (typeof row.value === 'boolean') return row.value ? 'yes' : 'no';
      if (typeof row.value !== 'number') return '-';
      return `${formatters.commasExact2Digits(row.value)}${
        row.unit ? ` ${row.unit}` : ''
      }`;
    },
  },
  { title: 'What it means', dataIndex: 'note', key: 'note' },
];

/** Whole-book limits, each saying what it measures and where it came from. */
const portfolioCheckColumns: ColumnsType<{
  key: string;
  label: string;
  unit: string;
  meaning: string;
  limit: number | null;
  source: string;
  derivation: string | null;
}> = [
  { title: 'Limit', dataIndex: 'label', key: 'label', width: 180 },
  {
    title: 'Value',
    key: 'limit',
    width: 130,
    align: 'right',
    render: (_, row) =>
      typeof row.limit === 'number'
        ? `${formatters.commasExact1Digit(row.limit)}${
            row.unit ? ` ${row.unit}` : ''
          }`
        : '-',
  },
  {
    title: 'Where it comes from',
    key: 'source',
    width: 230,
    render: (_, row) => (
      <>
        <Tag color={row.source === 'session' ? 'blue' : 'default'}>
          {row.source === 'session' ? 'this session' : 'reference'}
        </Tag>
        {row.derivation ? (
          <Typography.Text type="secondary">{row.derivation}</Typography.Text>
        ) : null}
      </>
    ),
  },
  { title: 'What it measures', dataIndex: 'meaning', key: 'meaning' },
];

const frameworkColumns = (names: {
  [code: string]: string;
}): ColumnsType<{
  code: string;
  scoring_soft_limit_m: number | null;
  stored_soft_limit_m?: number | null;
  pm_pretrade_cap_m: number | null;
  diverges: boolean;
  asset_class?: string | null;
  class_limit_pct?: number | null;
  margin_pct?: number | null;
  asset_count?: number | null;
  formula?: string | null;
}> => [
  {
    title: 'Instrument',
    dataIndex: 'code',
    key: 'code',
    render: (code: string) => (
      <Tooltip title={code}>
        <span>{names[code] || code}</span>
      </Tooltip>
    ),
  },
  {
    title: (
      <Tooltip title="The asset class this instrument belongs to. The percentage below is set ONCE for the whole class — there is no per-instrument setting anywhere in PM.">
        <span>Asset class</span>
      </Tooltip>
    ),
    key: 'asset_class',
    render: (_, row) =>
      row.asset_class || <Typography.Text type="secondary">-</Typography.Text>,
  },
  {
    title: (
      <Tooltip title="The class's share of the fund, from the session's Instrument limits card. Every instrument in the class inherits this same number.">
        <span>Class limit</span>
      </Tooltip>
    ),
    key: 'class_limit_pct',
    align: 'right',
    render: (_, row) =>
      typeof row.class_limit_pct === 'number'
        ? formatters.percent1Digit(row.class_limit_pct)
        : '-',
  },
  {
    title: (
      <Tooltip title="The class's margin requirement. Dividing by it is what turns a percentage of the fund into a much larger position: 5% margin means 20x.">
        <span>Margin</span>
      </Tooltip>
    ),
    key: 'margin_pct',
    align: 'right',
    render: (_, row) =>
      typeof row.margin_pct === 'number' && row.margin_pct !== 1
        ? formatters.percent1Digit(row.margin_pct)
        : <Typography.Text type="secondary">none</Typography.Text>,
  },
  {
    title: (
      <Tooltip title="The sum, with this session's numbers in it. Check it by hand.">
        <span>How the cap is worked out</span>
      </Tooltip>
    ),
    key: 'formula',
    width: 300,
    render: (_, row) =>
      row.formula ? (
        <Typography.Text type="secondary">{row.formula}</Typography.Text>
      ) : (
        '-'
      ),
  },
  {
    title: (
      <Tooltip
        title={
          'The most this instrument could be held before the score counts it ' +
          'as oversized. Each instrument is checked against its own copy of ' +
          'this number — two bonds do not share one allowance.'
        }
      >
        <span>Graded against $m</span>
      </Tooltip>
    ),
    key: 'scoring',
    align: 'right',
    render: (_, row) => num1(row.scoring_soft_limit_m),
  },
  {
    title: (
      <Tooltip
        title={
          'The most the simulator would actually let the trainee buy, checked ' +
          'on every order at the time they traded. This is the reality check: ' +
          'the graded number should equal it.'
        }
      >
        <span>PM pre-trade cap $m</span>
      </Tooltip>
    ),
    key: 'pm',
    render: (_, row) => num1(row.pm_pretrade_cap_m),
  },
  {
    title: (
      <Tooltip
        title={
          'Green means the trainee was graded against exactly the limit they ' +
          'traded under. Red means the two disagree — the score would punish ' +
          'or reward a position the simulator treated differently, and should ' +
          'not be trusted until it is fixed.'
        }
      >
        <span>Agree?</span>
      </Tooltip>
    ),
    key: 'diverges',
    render: (_, row) =>
      row.pm_pretrade_cap_m === null ? (
        '-'
      ) : row.diverges ? (
        <Tag color="red">diverges</Tag>
      ) : (
        <Tag color="green">match</Tag>
      ),
  },
];

const periodTitle = (row: AssessmentDebugPeriod) =>
  row.label || `P${row.index}`;

const DebugTab: React.FC = () => {
  const dispatch = useDispatch();
  const debug = useSelector(selectors.assessmentDebug);
  const validation = useSelector(selectors.assessmentValidation);
  const operations = useSelector(selectors.assessmentOperations);

  const [player, setPlayer] = useState<number>();

  const envelope = debug.envelope;
  const scenario = envelope?.scenario;
  const item = player !== undefined ? debug.items[player] : undefined;
  const periods = useMemo(() => item?.periods || [], [item]);

  const operationRunning = operations.recomputingOptimal;

  // React to each operation completion exactly once (seq increments per
  // completion; the ref swallows whatever result predates this mount): on
  // success the backend state changed, so re-fetch the debug envelope, the
  // selected player's ledger and the scenario validation.
  const handledOperationSeq = useRef(operations.lastResult?.seq);
  useEffect(() => {
    const result = operations.lastResult;
    if (!result || result.seq === handledOperationSeq.current) return;
    handledOperationSeq.current = result.seq;
    if (!result.ok) return;
    dispatch(fetchAssessmentDebug({}));
    if (player !== undefined) dispatch(fetchAssessmentDebug({ player }));
    if (scenario) dispatch(fetchAssessmentValidation({ scenario }));
  }, [dispatch, operations.lastResult, player, scenario]);

  // Envelope: fetch once. envelopeFetching === false means attempted (done or
  // failed) - never loop; Refresh/Retry re-dispatch unconditionally.
  useEffect(() => {
    if (debug.envelope || debug.envelopeFetching !== undefined) return;
    dispatch(fetchAssessmentDebug({}));
  }, [dispatch, debug.envelope, debug.envelopeFetching]);

  // Per-player ledger, cached by id with resultDetails-style markers.
  useEffect(() => {
    if (player === undefined) return;
    if (debug.items[player] || debug.fetching[player] !== undefined) return;
    dispatch(fetchAssessmentDebug({ player }));
  }, [dispatch, player, debug.items, debug.fetching]);

  // Scenario-level key validation (separate admin-only endpoint).
  useEffect(() => {
    if (!scenario) return;
    if (
      validation.scenario === scenario &&
      (validation.item || validation.fetching !== undefined)
    )
      return;
    dispatch(fetchAssessmentValidation({ scenario }));
  }, [dispatch, scenario, validation]);

  // Optimal targets arrive without units; borrow them from the player's held
  // positions where the codes overlap.
  const unitByCode = useMemo(() => {
    const units: { [code: string]: string } = {};
    periods.forEach(period =>
      (period.positions || []).forEach(position => {
        units[position.code] = position.unit;
      }),
    );
    return units;
  }, [periods]);

  const optimalLine = (line: AssessmentDebugOptimalLine) => {
    const unit = unitByCode[line.code];
    const base = `${line.code} ${formatters.commasExact1Digit(line.target)}${
      unit ? ` ${unit}` : ''
    }`;
    return line.util !== null && line.util !== undefined
      ? `${base} (util ${formatters.percent2Digit(line.util)})`
      : base;
  };

  const ledgerColumns: ColumnsType<AssessmentDebugPeriod> = useMemo(
    () => [
      {
        title: 'Period',
        key: 'period',
        render: (_, row) => (
          <Tooltip title={`${row.start_date} to ${row.end_date}`}>
            <span>{periodTitle(row)}</span>
          </Tooltip>
        ),
      },
      {
        title: 'Regime',
        dataIndex: 'regime',
        key: 'regime',
        render: (regime: string) => (regime ? <Tag>{regime}</Tag> : '-'),
      },
      {
        title: 'Positions',
        key: 'positions',
        render: (_, row) => (
          <Lines lines={(row.positions || []).map(positionLine)} />
        ),
      },
      {
        title: (
          <Tooltip
            title={
              'PCS = the benchmark\'s conviction score for that instrument in ' +
              'that period, on a -5 to +5 scale. It is produced up front from ' +
              'the period\'s news (not from anyone\'s trades): +5 means the ' +
              'news says be maximally long, -5 maximally short, 0 no signal. ' +
              'It is the starting point for the optimal book — see the ' +
              '"Optimal book" menu. Shown here only for instruments the ' +
              'player actually held.'
            }
          >
            <span>PCS (held)</span>
          </Tooltip>
        ),
        key: 'pcs',
        render: (_, row) => (
          <Lines
            lines={(row.positions || [])
              .filter(position => typeof row.pcs?.[position.code] === 'number')
              .map(
                position =>
                  `${position.code} ${signed2(row.pcs[position.code])}`,
              )}
          />
        ),
      },
      {
        title: (
          <Tooltip
            title={
              'What the optimal book held that period, and how much of THAT ' +
              'instrument\'s own limit it used. These percentages are each ' +
              'measured against a different limit, so they are not shares of ' +
              'one pot and do not add up to 100%. The "Optimal book" menu ' +
              'shows how every one of these was arrived at.'
            }
          >
            <span>Optimal</span>
          </Tooltip>
        ),
        key: 'optimal',
        render: (_, row) => (
          <Lines
            lines={(row.optimal || [])
              .filter(line => line.target)
              .map(optimalLine)}
          />
        ),
      },
      {
        title: (
          <Tooltip
            title={
              'What this book would lose in one standard bad day. Every ' +
              'instrument has a fixed shock defined up front (for example UK ' +
              '10y yields +100bp, US 10y +60bp, equities -10%, oil -15%); ' +
              'this is the total loss those shocks would cause. Shorts count ' +
              'too — the shock is applied against whichever way you are ' +
              'positioned.'
            }
          >
            <span>Stress $m</span>
          </Tooltip>
        ),
        key: 'stress_loss',
        className: 'assessment-report-num',
        render: (_, row) => num1(row.summary?.stress_loss),
      },
      {
        title: 'NAV $m',
        key: 'nav_pm',
        className: 'assessment-report-num',
        render: (_, row) => num1(row.nav_pm),
      },
      {
        title: 'ΔNAV $m',
        key: 'nav_delta',
        className: 'assessment-report-num',
        render: (_, row) => num1(row.nav_delta),
      },
      {
        title: (
          <Tooltip
            title={
              'The profit the scoring model attributes to the positions held ' +
              'in this period. It will not match the NAV change exactly, and ' +
              'that is expected: NAV is the real wallet (fees, financing, ' +
              'timing of every trade), while this replays the period\'s ' +
              'closing positions against the period\'s market move. Use NAV ' +
              'for "what did they make", this for "what did the score see".'
            }
          >
            <span>Model PnL $m</span>
          </Tooltip>
        ),
        key: 'pnl_model',
        className: 'assessment-report-num',
        render: (_, row) => num1(row.pnl_model),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [unitByCode],
  );

  // The limits in force, keyed for the aggregates header. Session-derived, so
  // the header always agrees with what scoring used.
  const frameworkByKey = useMemo(() => {
    const map: {
      [key: string]: {
        limit: number | null;
        label: string;
        unit: string;
        meaning: string;
        source: string;
        derivation: string | null;
      };
    } = {};
    (item?.risk_framework?.portfolio_rows || []).forEach(row => {
      map[row.key] = {
        limit: row.limit,
        label: row.label,
        unit: row.unit,
        meaning: row.meaning,
        source: row.source,
        derivation: row.derivation,
      };
    });
    return map;
  }, [item]);

  // Orientation strip: is this player graded on the session's own limits, and
  // did their book ever sit outside one? Both are the first questions asked
  // when a score looks wrong, so they belong above the fold rather than three
  // sections down.
  const frameworkMode = item?.risk_framework?.portfolio_session
    ? (item.risk_framework.portfolio_rows || []).some(r => r.source === 'session')
      ? 'session'
      : 'reference'
    : 'reference';

  const breachCount = useMemo(
    () =>
      periods.filter(period =>
        AGGREGATES.some(spec => {
          if (!spec.limitKey) return false;
          const limit = frameworkByKey[spec.limitKey]?.limit;
          const value = period.summary?.[spec.key];
          return (
            typeof limit === 'number' &&
            typeof value === 'number' &&
            Math.abs(value) > limit
          );
        }),
      ).length,
    [periods, frameworkByKey],
  );

  const aggregateColumns: ColumnsType<AssessmentDebugPeriod> = useMemo(
    () => [
      {
        title: 'Period',
        key: 'period',
        render: (_: unknown, row: AssessmentDebugPeriod) => periodTitle(row),
      },
      ...AGGREGATES.map(spec => {
        const check = spec.limitKey ? frameworkByKey[spec.limitKey] : undefined;
        const limit = check?.limit ?? undefined;
        const tip = spec.limitKey
          ? [
              check?.meaning,
              typeof limit === 'number'
                ? `Limit ${formatters.commasExact1Digit(limit)} ${
                    check?.unit ?? ''
                  } — ${
                    check?.source === 'session'
                      ? `set by this session (${check?.derivation ?? 'session setting'})`
                      : 'a reference value: PM has no setting for this yet'
                  }`
                : 'No limit in force for this session.',
            ]
              .filter(Boolean)
              .join(' ')
          : WEIGHTED_EXCESS_TIP;
        // Two lines: the plain-language name (from the backend where it has
        // one), then the limit spelled out. The old single line read
        // "Gross cash $m / 700", which never said that 700 WAS the limit.
        return {
          title: (
            <Tooltip title={tip}>
              <div className="assessment-debug-agg-head">
                <div className="assessment-debug-agg-name">
                  {check?.label || spec.label}
                </div>
                <div className="assessment-debug-agg-limit">
                  {typeof limit === 'number'
                    ? `limit ${formatters.commasExact1Digit(limit)}${
                        check?.unit ? ` ${check.unit}` : ''
                      }`
                    : spec.limitKey
                      ? 'no limit'
                      : 'score, not a limit'}
                </div>
              </div>
            </Tooltip>
          ),
          key: spec.key as string,
          className: 'assessment-report-num',
          render: (_: unknown, row: AssessmentDebugPeriod) => {
            const value = row.summary?.[spec.key];
            if (typeof value !== 'number') return '-';
            const exceeds = typeof limit === 'number' && Math.abs(value) > limit;
            return (
              <span className={exceeds ? 'assessment-debug-exceeds' : undefined}>
                {formatters.commasExact1Digit(value)}
              </span>
            );
          },
        };
      }),
    ],
    [frameworkByKey],
  );

  // Distinct realized-move sources per instrument, from the validation
  // per-period detail rows.
  const moveSources = useMemo(() => {
    const sources: { [instrument: string]: string[] } = {};
    (validation.item?.periods || []).forEach(row => {
      if (!row.move_source) return;
      const list = sources[row.instrument] || (sources[row.instrument] = []);
      if (!list.includes(row.move_source)) list.push(row.move_source);
    });
    return sources;
  }, [validation.item]);

  const validationColumns: ColumnsType<AssessmentValidationInstrument> =
    useMemo(
      () => [
        {
          title: 'Instrument',
          dataIndex: 'instrument',
          key: 'instrument',
        },
        {
          title: (
            <Tooltip
              title={
                'Correlation between what the model expected the instrument ' +
                'to do and what it actually did, -1 to +1. Above ~0.3 is ' +
                'healthy; near 0 means the scenario\'s view of this ' +
                'instrument carried no information; negative means it was ' +
                'consistently backwards.'
              }
            >
              <span>Corr</span>
            </Tooltip>
          ),
          dataIndex: 'correlation',
          key: 'correlation',
          className: 'assessment-report-num',
          render: num2,
        },
        {
          title: (
            <Tooltip
              title={
                'How often the model got the DIRECTION right, ignoring size. ' +
                '50% is a coin flip — anything at or below that for an ' +
                'instrument the score leans on is worth investigating.'
              }
            >
              <span>Hit rate</span>
            </Tooltip>
          ),
          dataIndex: 'directional_hit_rate',
          key: 'directional_hit_rate',
          className: 'assessment-report-num',
          render: (value: number | null) =>
            typeof value === 'number' ? formatters.percent0Digits(value) : '-',
        },
        {
          title: (
            <Tooltip title="How many periods this is based on. A hit rate from two or three periods means very little.">
              <span>N</span>
            </Tooltip>
          ),
          key: 'n',
          className: 'assessment-report-num',
          render: (_, row) => (
            <Tooltip
              title={`${row.hits} directional hits over ${row.eligible} eligible periods (${row.periods_with_move} periods with a realized move)`}
            >
              <span>{`${row.hits} / ${row.eligible}`}</span>
            </Tooltip>
          ),
        },
        {
          title: (
            <Tooltip
              title={
                'Where the actual market move came from. Expected values are ' +
                "the scenario's own price history; anything unexpected here " +
                'means the instrument was matched to the wrong data.'
              }
            >
              <span>Move source</span>
            </Tooltip>
          ),
          key: 'move_source',
          render: (_, row) =>
            (moveSources[row.instrument] || []).join(', ') || '-',
        },
      ],
      [moveSources],
    );

  if (!envelope) {
    if (debug.envelopeFetching === true) {
      return (
        <Table dataSource={[]} columns={[]} loading pagination={false} />
      );
    }
    return (
      <Alert
        type="warning"
        showIcon
        message="Debug data unavailable"
        description="Could not load the assessment debug envelope. The backend endpoint may not be deployed yet."
        action={
          <Button size="small" onClick={() => dispatch(fetchAssessmentDebug({}))}>
            Retry
          </Button>
        }
      />
    );
  }

  const mapRows = Object.entries(envelope.instrument_map || {}).map(
    ([code, assetIds]) => ({ code, assetIds }),
  );

  const ledgerLoading = player !== undefined && debug.fetching[player] === true;
  const ledgerFailed =
    player !== undefined && !item && debug.fetching[player] === false;

  return (
    <div className="assessment-report-tab">
      <div className="assessment-report-player-row">
        <Tag color={statusColor(envelope.link_status)}>
          {envelope.link_status || 'unknown'}
        </Tag>
        <Typography.Text strong>{envelope.scenario}</Typography.Text>
        <Typography.Text type="secondary">
          model v{envelope.model_version}
        </Typography.Text>
        <Button
          size="small"
          onClick={() => {
            dispatch(fetchAssessmentDebug({}));
            if (player !== undefined) dispatch(fetchAssessmentDebug({ player }));
          }}
        >
          Refresh
        </Button>
      </div>
      {envelope.link_error && (
        <Alert
          type="error"
          showIcon
          message="Link error"
          description={envelope.link_error}
        />
      )}
      <div className="assessment-report-player-row assessment-debug-player-row">
        <Typography.Text type="secondary">Player</Typography.Text>
        <Select
          style={{ width: 320 }}
          placeholder="Select a player"
          value={player}
          onChange={setPlayer}
          showSearch
          optionFilterProp="label"
          options={(envelope.players || []).map(p => ({
            value: p.id,
            label: p.utilized ? p.username : `${p.username} (unutilized)`,
          }))}
        />
        {player !== undefined && item?.scoring && (
          <Tag color="blue">{item.scoring.rating}</Tag>
        )}
      </div>
      {player !== undefined && item && (
        <div className="assessment-debug-stats">
          <StatCard
            label="Composite"
            value={num2(item.scoring?.official_composite)}
            hint="mean of P1, P2, P3"
            // What this number was earned on. Pillar 1 is a third of it and
            // needs no trade at all, so a real composite can belong to
            // somebody who never opened the order ticket — the panel that
            // explains a score in full has to say which it was. A score
            // graded before the flag existed renders nothing here.
            extra={
              <>
                <ParticipationNote
                  participation={item.scoring?.participation}
                />
                <ConvictionCoverageNote
                  coverage={item.scoring?.conviction_coverage}
                />
              </>
            }
          />
          <StatCard
            label="Rank"
            value={
              item.scoring?.rank
                ? `${item.scoring.rank} / ${
                    (item.players || []).filter(p => p.utilized).length || '-'
                  }`
                : '-'
            }
            hint="among players who traded"
          />
          <StatCard label="Periods" value={periods.length} hint="in this scenario" />
          <StatCard
            label="Limits"
            value={
              frameworkMode === 'session' ? (
                <Typography.Text type="success">this session</Typography.Text>
              ) : (
                <Typography.Text type="warning">reference</Typography.Text>
              )
            }
            hint={
              frameworkMode === 'session'
                ? 'graded on what they traded under'
                : 'session limits not derivable'
            }
          />
          <StatCard
            label="Periods over a limit"
            value={
              breachCount > 0 ? (
                <span className="assessment-debug-exceeds">{breachCount}</span>
              ) : (
                0
              )
            }
            hint="book outside a portfolio limit"
          />
        </div>
      )}
      {ledgerFailed && (
        <Alert
          type="warning"
          showIcon
          message="Could not load this player's ledger. Refresh above to retry."
        />
      )}
      <Collapse
        className="assessment-debug-sections"
        defaultActiveKey={['ledger']}
        items={[
                {
                  key: 'ledger',
                  label: sectionLabel(
                    'Period ledger',
                    'what the player held each period, and what it made',
                    periods.length ? `${periods.length} periods` : undefined,
                  ),
                  children:
                    player === undefined ? (
                      <Empty
                        description={
                          <Typography.Text type="secondary">
                            Select a player above to inspect their period ledger.
                          </Typography.Text>
                        }
                      />
                    ) : (
                      <>
                        <Table
                          dataSource={periods}
                          columns={ledgerColumns}
                          rowKey="index"
                          bordered
                          size="small"
                          loading={ledgerLoading}
                          pagination={false}
                          sticky
                          scroll={{ x: true }}
                        />
                        <div className="assessment-debug-footnote">
                          ΔNAV is the NAV change taken from the PM books (source
                          of truth). Model PnL is the scoring engine&apos;s
                          convention, shown for context only.
                          {item?.totals &&
                            ` Totals: model PnL ${formatters.commasExact1Digit(
                              item.totals.total_pnl_m,
                            )} $m, ending NAV ${formatters.commasExact1Digit(
                              item.totals.ending_nav_m,
                            )} $m.`}
                        </div>
                      </>
                    ),
                },
                ...(periods.length === 0 || player === undefined
                  ? []
                  : [
                {
                  key: 'benchmark',
                  label: sectionLabel(
                    'Optimal book',
                    'the benchmark every score is measured against',
                  ),
                  children: (
                    <>
                      <Typography.Paragraph>
                        <b>What this is.</b> The book the model says was the
                        right one to hold. It is not a trainee&apos;s book and
                        it is not hindsight-perfect — it is what the news of
                        the period justified, sized to fit this
                        session&apos;s risk limits. Pillars 2 and 3 score a
                        trainee by how close they came to it.
                      </Typography.Paragraph>
                      <Typography.Paragraph type="secondary">
                        <b>Where it comes from, in order.</b> (1) The
                        period&apos;s real news headlines are read up front
                        and each is scored for how much it moves each
                        instrument. (2) Those combine into one conviction
                        number per instrument, the <b>PCS</b>, from -5 to +5.
                        (3) The PCS maps to a position size — below 0.35 in
                        absolute terms nothing at all, 1.5 → 35% of the
                        instrument&apos;s limit, 3.5 → 70%, 5 → 100% — then
                        shrunk when the underlying analyst views disagreed.
                        That is the <b>Wanted</b> column. (4) The whole book
                        is then squeezed until it fits every portfolio budget
                        listed underneath the table, giving <b>Final</b>.
                        Nothing here looks at what actually happened next.
                      </Typography.Paragraph>
                      <Typography.Paragraph type="secondary">
                        Every headline behind step 1 is listed under the
                        &quot;Pillar 1 evidence&quot; menu; every limit behind
                        step 4 is under &quot;Risk framework&quot;, with the
                        session setting it was derived from.
                      </Typography.Paragraph>
                      <Alert
                        type="info"
                        showIcon
                        message="Utilisation does not add up to 100% — and should not"
                        description={
                          'Final is each instrument\'s use of ITS OWN limit. ' +
                          'Those limits are not slices of one pot: PM sets one ' +
                          'percentage per asset class and then checks each ' +
                          'instrument against it separately, so every bond has ' +
                          'its own full allowance. Several lines sitting at ' +
                          '70-100% at once is normal and is not 170% of ' +
                          'anything. The column that DOES add up is "% of ' +
                          'budget": each line\'s share of the portfolio stress ' +
                          'budget, totalling the stress row below. See "Risk ' +
                          'framework" for how each cap is worked out.'
                        }
                      />
                      <Tabs
                        size="small"
                        tabPosition="top"
                        items={periods.map(period => ({
                          key: String(period.index),
                          label: periodTitle(period),
                          children: period.optimal_detail ? (
                            <>
                              {period.optimal_detail.stale_targets?.length ? (
                                <Alert
                                  type="error"
                                  showIcon
                                  className="assessment-debug-limitation"
                                  message="This benchmark was built under older limits"
                                  description={
                                    <>
                                      {`The stored optimal book for this period no longer agrees with the limits now in force (${period.optimal_detail.stale_targets.join(
                                        ', ',
                                      )}). Until it is rebuilt, the sizes below are not the ones this session's limits imply, and Pillars 2 and 3 are comparing players against the older book.`}
                                      <div>
                                        Fix: run{' '}
                                        <Typography.Text strong>
                                          Recompute optimal targets
                                        </Typography.Text>{' '}
                                        under Operations at the bottom of this
                                        page. Scores will change.
                                      </div>
                                    </>
                                  }
                                />
                              ) : null}
                              <Typography.Paragraph type="secondary">
                                {`Targets: ${period.optimal_detail.targets_source} · total stress ${num1(
                                  period.optimal_detail.stress_total_m,
                                )} of ${num1(
                                  period.optimal_detail.stress_budget_m,
                                )} $m budget (${pct1(
                                  period.optimal_detail.stress_total_share,
                                )})`}
                              </Typography.Paragraph>
                              <Table
                                dataSource={period.optimal_detail.lines}
                                columns={optimalDetailColumns(
                                  period.optimal_detail.tooltips || {},
                                  item?.display_names || {},
                                )}
                                rowKey="code"
                                bordered
                                size="small"
                                pagination={false}
                                tableLayout="fixed"
                                scroll={{ x: true }}
                                expandable={{
                                  rowExpandable: line =>
                                    Boolean(line.steps?.length),
                                  expandedRowRender: line => (
                                    <ol className="assessment-debug-steps">
                                      {(line.steps || []).map(stepRow => (
                                        <li key={stepRow.n}>
                                          <Typography.Text strong>
                                            {stepRow.title}
                                          </Typography.Text>
                                          <div>
                                            <Typography.Text type="secondary">
                                              {stepRow.detail}
                                            </Typography.Text>
                                          </div>
                                        </li>
                                      ))}
                                    </ol>
                                  ),
                                }}
                              />
                              <Typography.Text type="secondary">
                                Expand any row to see that position worked out
                                step by step, with this period&apos;s actual
                                numbers.
                              </Typography.Text>
                              <Typography.Text
                                strong
                                className="assessment-debug-subhead"
                              >
                                Portfolio budgets the book had to fit
                              </Typography.Text>
                              <div className="assessment-debug-budgets">
                                {period.optimal_detail.budgets.map(budget => (
                                  <BudgetBar key={budget.key} budget={budget} />
                                ))}
                              </div>
                              {period.optimal_detail.shape?.length ? (
                                <>
                                  <Typography.Text
                                    strong
                                    className="assessment-debug-subhead"
                                  >
                                    Shape of the book
                                  </Typography.Text>
                                  <Typography.Paragraph type="secondary">
                                    Not how big the book is — what kind of book
                                    it is. These are what Pillar 3 compares when
                                    it asks whether a player took risk the same
                                    WAY the benchmark did.
                                  </Typography.Paragraph>
                                  <Table
                                    dataSource={period.optimal_detail.shape}
                                    columns={shapeColumns}
                                    rowKey="key"
                                    bordered
                                    size="small"
                                    pagination={false}
                                    tableLayout="fixed"
                                  />
                                </>
                              ) : null}
                              <Collapse
                                size="small"
                                items={[
                                  {
                                    key: 'raw',
                                    label:
                                      'Every raw number for this period (engineering)',
                                    children: (
                                      <pre className="assessment-debug-raw">
                                        {JSON.stringify(
                                          period.benchmark_portfolio,
                                          null,
                                          2,
                                        )}
                                      </pre>
                                    ),
                                  },
                                ]}
                              />
                            </>
                          ) : (
                            <pre className="assessment-debug-raw">
                              {JSON.stringify(period.benchmark_portfolio, null, 2)}
                            </pre>
                          ),
                        }))}
                      />
                    </>
                  ),
                },
                {
                  key: 'aggregates',
                  label: sectionLabel(
                    'Aggregates vs limits',
                    'the whole book each period, against what was allowed',
                    breachCount > 0 ? (
                      <Tag color="orange">{`${breachCount} period(s) over`}</Tag>
                    ) : (
                      <Tag color="green">inside every limit</Tag>
                    ),
                  ),
                  children: (
                    <>
                      <Typography.Paragraph type="secondary">
                        One row per period, showing the whole book measured
                        every way the risk framework checks it, against the
                        limit that applied. This is where to look for &quot;was
                        this player ever oversized, and on what&quot;.
                      </Typography.Paragraph>
                      <Table
                        dataSource={periods}
                        columns={aggregateColumns}
                        rowKey="index"
                        bordered
                        size="small"
                        pagination={false}
                        sticky
                        scroll={{ x: true }}
                      />
                      <Typography.Text type="secondary">
                        Each column header shows the limit in force; hover it to
                        see what the limit measures and where it came from. A
                        figure past its limit is shown in orange.
                      </Typography.Text>
                    </>
                  ),
                },
                {
                  key: 'signal',
                  label: sectionLabel(
                    'Pillar 1 evidence',
                    'every headline, what it moved, and what the player linked',
                  ),
                  children: (
                    <>
                      <Typography.Paragraph type="secondary">
                        For each headline: which instruments it genuinely moves
                        (|ICS| &ge; {RELEVANT_ICS}), and whether the candidate
                        linked them. Expand a row to see every instrument and
                        the reason it counted as a hit, false positive, miss or
                        correct pass. Scoring weights each pair by |ICS| — the
                        counts are the audit trail, not the score itself.
                      </Typography.Paragraph>
                      <Tabs
                        size="small"
                        tabPosition="top"
                        items={periods.map(period => ({
                          key: String(period.index),
                          label: periodTitle(period),
                          children: period.signal ? (
                            <>
                              <div className="assessment-debug-counts">
                                <Tooltip title="Linked it, and the news does move it">
                                  <Tag color="green">{`${period.signal.mapping_counts.tp} hits`}</Tag>
                                </Tooltip>
                                <Tooltip title="Linked it, but the news barely moves it">
                                  <Tag color="orange">{`${period.signal.mapping_counts.fp} false`}</Tag>
                                </Tooltip>
                                <Tooltip title="The news moves it and it was not linked">
                                  <Tag color="red">{`${period.signal.mapping_counts.fn} missed`}</Tag>
                                </Tooltip>
                                <Tooltip title="Correctly left alone — the news does not move it">
                                  <Tag>{`${period.signal.mapping_counts.tn} correctly ignored`}</Tag>
                                </Tooltip>
                                <Typography.Text type="secondary">
                                  {`${period.signal.mapping_counts.assignments} link(s) made · precision ${num2(
                                    period.signal.mapping_counts.precision,
                                  )} · recall ${num2(
                                    period.signal.mapping_counts.recall,
                                  )} · F1 ${num2(period.signal.mapping_counts.f1_unweighted)}`}
                                </Typography.Text>
                              </div>
                              <Table
                                dataSource={period.signal.news}
                                columns={newsColumns}
                                rowKey="news_code"
                                bordered
                                size="small"
                                pagination={false}
                                tableLayout="fixed"
                                expandable={{
                                  expandedRowRender: news => (
                                    <Table
                                      dataSource={news.verdicts}
                                      columns={verdictColumns}
                                      rowKey="code"
                                      size="small"
                                      pagination={false}
                                      tableLayout="fixed"
                                    />
                                  ),
                                }}
                              />
                              <Typography.Text strong className="assessment-debug-subhead">
                                Conviction sliders vs the benchmark view
                              </Typography.Text>
                              <Table
                                dataSource={period.signal.conviction}
                                columns={convictionColumns}
                                rowKey="code"
                                bordered
                                size="small"
                                pagination={false}
                                tableLayout="fixed"
                              />
                            </>
                          ) : (
                            <Empty description="no signal detail" />
                          ),
                        }))}
                      />
                    </>
                  ),
                },
                {
                  key: 'scoring',
                  label: sectionLabel(
                    'Scoring',
                    'how each pillar score was built, metric by metric',
                    item?.scoring ? (
                      <Tag color="blue">{`composite ${num2(
                        item.scoring.official_composite,
                      )}`}</Tag>
                    ) : undefined,
                  ),
                  children: item?.scoring ? (
                    <>
                      <div className="assessment-debug-stats">
                        {[
                          {
                            key: '1',
                            label: 'P1 Signal',
                            hint: 'reading the news',
                          },
                          {
                            key: '2',
                            label: 'P2 Positioning',
                            hint: 'acting on it',
                          },
                          {
                            key: '3',
                            label: 'P3 Risk',
                            hint: 'sizing and limits',
                          },
                        ].map(pillar => (
                          <StatCard
                            key={pillar.key}
                            label={pillar.label}
                            value={num2(item.scoring?.pillars[pillar.key])}
                            hint={pillar.hint}
                          />
                        ))}
                        <StatCard
                          label="Composite"
                          value={
                            <Typography.Text strong>
                              {num2(item.scoring.official_composite)}
                            </Typography.Text>
                          }
                          hint={`${item.scoring.rating} · ranked ${
                            item.scoring.rank ?? '-'
                          } of ${
                            (item.players || []).filter(p => p.utilized)
                              .length || '-'
                          }`}
                        />
                      </div>
                      <Typography.Paragraph type="secondary">
                        {item.scoring.official_formula}
                        {item.scoring.note ? ` — ${item.scoring.note}` : ''}
                        {` Recomputed here as ${num2(
                          item.scoring.official_composite_check,
                        )} — it must match the stored composite above.`}
                      </Typography.Paragraph>
                      <Typography.Paragraph type="secondary">
                        {`Not counted in the composite: P4 ${num2(
                          item.scoring.pillars['4_provisional'],
                        )}, P5 ${num2(
                          item.scoring.pillars['5_provisional'],
                        )}. They are provisional because the platform does not
                        yet capture everything they need.`}
                        <br />
                        <Tag color="blue">{item.scoring.model_version}</Tag>
                        {item.scoring.computed_at
                          ? `computed ${item.scoring.computed_at}`
                          : ''}
                      </Typography.Paragraph>
                      <Typography.Text strong className="assessment-debug-subhead">
                        Per-period detail
                      </Typography.Text>
                      <Typography.Paragraph type="secondary">
                        Pick a period, then open the pillar you want. Each
                        pillar&apos;s score for that period is on its header, so
                        you can see where a period went wrong without opening
                        anything.
                      </Typography.Paragraph>
                      <Tabs
                        size="small"
                        tabPosition="top"
                        items={Object.entries(item.scoring.explained || {})
                          .sort((a, b) => Number(a[0]) - Number(b[0]))
                          .map(([periodIndex, pillars]) => ({
                            key: periodIndex,
                            label:
                              periods.find(
                                period => String(period.index) === periodIndex,
                              )?.label || `P${periodIndex}`,
                            children: (
                              <Collapse
                                size="small"
                                items={Object.entries(pillars)
                                  .sort((a, b) => a[0].localeCompare(b[0]))
                                  .map(([pillar, detail]) => ({
                                    key: pillar,
                                    label: (
                                      <div className="assessment-debug-section-label">
                                        <div>
                                          <Typography.Text strong>
                                            {`P${pillar} — ${detail.title}`}
                                          </Typography.Text>
                                          {!detail.official && (
                                            <Tag color="default">
                                              provisional
                                            </Tag>
                                          )}
                                        </div>
                                        <Typography.Text strong>
                                          {num2(detail.score)}
                                        </Typography.Text>
                                      </div>
                                    ),
                                    children: (
                                      <Table
                                        dataSource={detail.metrics}
                                        columns={metricColumns}
                                        rowKey="key"
                                        size="small"
                                        bordered
                                        pagination={false}
                                        tableLayout="fixed"
                                      />
                                    ),
                                  }))}
                              />
                            ),
                          }))}
                      />
                    </>
                  ) : (
                    <Empty description="no stored result for this player" />
                  ),
                },
                {
                  key: 'limitations',
                  label: sectionLabel(
                    'Known limitations',
                    'where the model still uses a value nobody set',
                    item?.risk_framework?.known_limitations?.length ? (
                      <Tag color="warning">
                        {`${item.risk_framework.known_limitations.length} to review`}
                      </Tag>
                    ) : undefined,
                  ),
                  children: item?.risk_framework?.known_limitations?.length ? (
                    <>
                      <Typography.Paragraph type="secondary">
                        Everything else on this page explains how a number was
                        produced. This menu is the opposite: the places where
                        the model is still using a value nobody set on the
                        session. They are not bugs and nothing here is hidden
                        from the scores — but they are the assumptions to
                        challenge first if a result looks wrong.
                      </Typography.Paragraph>
                      {item.risk_framework.known_limitations.map(limitation => (
                        <Alert
                          key={limitation.key}
                          type="warning"
                          showIcon
                          className="assessment-debug-limitation"
                          message={limitation.title}
                          description={
                            <div className="assessment-debug-limitation-body">
                              <div>
                                <Typography.Text strong>
                                  What it is:{' '}
                                </Typography.Text>
                                <Typography.Text>
                                  {limitation.what}
                                </Typography.Text>
                              </div>
                              <div>
                                <Typography.Text strong>
                                  Why it is like this:{' '}
                                </Typography.Text>
                                <Typography.Text>
                                  {limitation.why}
                                </Typography.Text>
                              </div>
                              <div>
                                <Typography.Text strong>
                                  What it can affect:{' '}
                                </Typography.Text>
                                <Typography.Text>
                                  {limitation.impact}
                                </Typography.Text>
                              </div>
                              <div>
                                <Typography.Text strong>
                                  Plan:{' '}
                                </Typography.Text>
                                <Typography.Text>
                                  {limitation.plan}
                                </Typography.Text>
                              </div>
                            </div>
                          }
                        />
                      ))}
                    </>
                  ) : (
                    <Empty description="no limitations recorded" />
                  ),
                },
                {
                  key: 'framework',
                  label: sectionLabel(
                    'Risk framework',
                    'the limits used to grade, and where each came from',
                    item?.risk_framework?.instrument?.some(i => i.diverges) ? (
                      <Tag color="red">diverges from PM</Tag>
                    ) : (
                      <Tag color="green">matches PM</Tag>
                    ),
                  ),
                  children: item?.risk_framework ? (
                    <>
                      <Typography.Paragraph type="secondary">
                        Read this top to bottom: what the trainer set, the
                        per-instrument caps that follow from it, then the
                        whole-book limits grading uses. The rule is that a
                        trading limit is a grading limit — anything the
                        simulator let the trainee do must not be marked as a
                        breach. A &quot;diverges&quot; flag means the two
                        disagree and the score should not be trusted.
                      </Typography.Paragraph>
                      <Typography.Text strong className="assessment-debug-subhead">
                        1. What the trainer set on the session
                      </Typography.Text>
                      <Table
                        dataSource={item.risk_framework.session_settings || []}
                        columns={settingColumns}
                        rowKey="label"
                        bordered
                        size="small"
                        pagination={false}
                        tableLayout="fixed"
                      />
                      <Typography.Text strong className="assessment-debug-subhead">
                        2. The cap each instrument is checked against
                      </Typography.Text>
                      <Alert
                        type="info"
                        showIcon
                        className="assessment-debug-limitation"
                        message="Set per asset class. Enforced per instrument."
                        description={
                          <div className="assessment-debug-limitation-body">
                            <div>
                              There is <b>no per-instrument setting anywhere in
                              PM</b>. A trainer sets one percentage for a whole
                              asset class — &quot;Bonds 100%&quot; — and every
                              bond inherits that same number. The card in the
                              session settings is titled &quot;Instrument
                              limits&quot;, which is where most of the confusion
                              comes from: it configures classes.
                            </div>
                            <div>
                              What <i>is</i> per instrument is the{' '}
                              <b>checking</b>. When an order is placed, PM
                              applies the class number to that one instrument&apos;s
                              position on its own. So six bonds each get their
                              own full allowance rather than sharing one — which
                              is why every bond below shows the same cap, and
                              why those caps do not add up to anything
                              meaningful.
                            </div>
                            <div>
                              The limit that <i>is</i> shared across a whole
                              class is the pooled book in the next table.
                            </div>
                          </div>
                        }
                      />
                      <Table
                        dataSource={item.risk_framework.instrument}
                        columns={frameworkColumns(item.display_names || {})}
                        rowKey="code"
                        bordered
                        size="small"
                        pagination={false}
                      />
                      <Typography.Text strong className="assessment-debug-subhead">
                        3. Whole-book limits used to grade
                      </Typography.Text>
                      <Typography.Paragraph type="secondary">
                        Blue rows are derived from this session, so grading uses
                        the same numbers the trainee traded under. Grey rows are
                        risk checks PM has no setting for yet — they keep the
                        model&apos;s reference values.
                      </Typography.Paragraph>
                      <Table
                        dataSource={item.risk_framework.portfolio_rows || []}
                        columns={portfolioCheckColumns}
                        rowKey="key"
                        bordered
                        size="small"
                        pagination={false}
                        tableLayout="fixed"
                      />
                      <Collapse
                        size="small"
                        items={[
                          {
                            key: 'raw',
                            label: 'Raw values (engineering)',
                            children: (
                              <pre className="assessment-debug-raw">
                                {JSON.stringify(
                                  {
                                    session_book_limits:
                                      item.risk_framework.portfolio_session,
                                    portfolio_checks_used:
                                      item.risk_framework.portfolio_scoring,
                                    pooled_caps_m:
                                      item.risk_framework.pooled_caps_m,
                                  },
                                  null,
                                  2,
                                )}
                              </pre>
                            ),
                          },
                        ]}
                      />
                    </>
                  ) : (
                    <Empty description="no framework detail" />
                  ),
                },
                  ]),
          {
            key: 'validation',
            label: sectionLabel(
              'Key validation',
              'is the scenario itself sound? (not about any player)',
            ),
            children: (
              <>
                <Typography.Paragraph type="secondary">
                  A sanity check on the scenario itself, not on any trainee. It
                  asks whether each instrument&apos;s assumed sensitivity
                  matches how much it actually moved over the scenario: if the
                  model thinks a bond moves half as much as it really did,
                  every score built on it is off. Healthy means the assumed and
                  observed scales are close and the move source is the expected
                  one; large gaps are worth investigating before trusting the
                  results.
                </Typography.Paragraph>
                <Table
                  dataSource={validation.item?.instruments || []}
                  columns={validationColumns}
                  rowKey="instrument"
                  bordered
                  size="small"
                  loading={validation.fetching === true}
                  pagination={false}
                />
                {validation.item?.summary?.note && (
                  <Typography.Text type="secondary">
                    {validation.item.summary.note}
                  </Typography.Text>
                )}
              </>
            ),
          },
          {
            key: 'resolver',
            label: sectionLabel(
              'Resolver map',
              'which PM assets each engine role resolves to',
              (envelope.map_problems || []).length ? (
                <Tag color="red">{`${envelope.map_problems.length} problem(s)`}</Tag>
              ) : undefined,
            ),
            children: (
              <>
                <Typography.Paragraph type="secondary">
                  The scoring model speaks in roles (US10Y, SPY); a session
                  holds specific PM assets. This is the mapping between them. A
                  role listing several ids is normal — a bond that rolled over
                  during the scenario is several assets playing one role.
                  Problems here mean a role found no asset, so nothing about it
                  can be scored.
                </Typography.Paragraph>
                <Table
                  dataSource={mapRows}
                  rowKey="code"
                  bordered
                  size="small"
                  pagination={false}
                  columns={[
                    { title: 'Role', dataIndex: 'code', key: 'code' },
                    {
                      title: 'PM asset ids',
                      key: 'assetIds',
                      className: 'assessment-report-num',
                      render: (_: unknown, row: { assetIds: number[] }) =>
                        (row.assetIds || []).join(', ') || '-',
                    },
                  ]}
                />
                {(envelope.map_problems || []).length > 0 && (
                  <ul className="assessment-debug-problems">
                    {envelope.map_problems.map(problem => (
                      <li key={problem} className="assessment-debug-exceeds">
                        {problem}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            ),
          },
        ]}
      />
      <Card size="small" title="Operations">
        <div className="assessment-report-player-row">
          <Popconfirm
            title="Rebuild this session's optimal book?"
            description={
              <div style={{ maxWidth: 420 }}>
                Solves every period again under THIS session&apos;s limits and
                stores the result on this session only — other sessions of the
                same scenario are untouched. Player scores for Pillars 2 and 3
                will change, because the book they are compared against
                changes.
              </div>
            }
            okText="Rebuild"
            onConfirm={() =>
              dispatch(recomputeAssessmentOptimal({ scope: 'session' }))
            }
            disabled={operationRunning}
          >
            <Button
              size="small"
              danger
              loading={operations.recomputingOptimal}
              disabled={operationRunning}
            >
              Recompute optimal targets
            </Button>
          </Popconfirm>
          <Typography.Text type="secondary">
            Rebuilds the optimal book for this session under the limits it
            actually configured. This is the fix when the Optimal book menu
            warns that the benchmark predates the current limits.
          </Typography.Text>
        </div>
        {operations.lastResult && (
          <Alert
            type={operations.lastResult.ok ? 'success' : 'error'}
            showIcon
            message={operations.lastResult.message}
            description={operations.lastResult.description}
          />
        )}
      </Card>
    </div>
  );
};

export default DebugTab;
